import { useCallback, useEffect, useRef } from "react";

/**
 * useReveal — retorna uma callback ref que registra o elemento em um
 * IntersectionObserver assim que ele entra no DOM. Robusto contra
 * componentes que fazem early return / placeholder enquanto aguardam
 * dados assíncronos (o observer é registrado quando o .reveal aparece,
 * não apenas na montagem inicial do componente).
 *
 * Regra editorial: se `IntersectionObserver` não estiver disponível
 * (ex: Safari muito antigo), aplica `is-visible` imediatamente para
 * garantir que o conteúdo NUNCA fique invisível para o usuário.
 */
export default function useReveal(options = {}) {
  const observerRef = useRef(null);
  const currentEl = useRef(null);
  const threshold = options.threshold ?? 0.12;
  const rootMargin = options.rootMargin ?? "0px 0px -60px 0px";

  const setRef = useCallback(
    (el) => {
      // limpa observer anterior (caso o elemento tenha sido desmontado)
      if (observerRef.current && currentEl.current) {
        observerRef.current.unobserve(currentEl.current);
      }
      currentEl.current = el;
      if (!el) return;

      // Fallback: se IO indisponível, revela imediatamente
      if (typeof IntersectionObserver === "undefined") {
        el.classList.add("is-visible");
        return;
      }

      // (Re)cria observer para o novo elemento
      if (!observerRef.current) {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add("is-visible");
                observerRef.current?.unobserve(entry.target);
              }
            });
          },
          { threshold, rootMargin }
        );
      }
      observerRef.current.observe(el);
    },
    [threshold, rootMargin]
  );

  // Cleanup no unmount do componente que usa o hook
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);

  return setRef;
}
